import React from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Stack,
  Paper,
  MenuItem,
  Avatar,
  Chip,
  Tooltip,
} from '@mui/material';
import { Search, RestaurantMenuOutlined } from '@mui/icons-material';
import { ImageUpload } from './FormComponents';
import { menuPalette } from '../utils/dashboardHelpers';

const HEADER_COLOR_PRESETS = ['#1f4fbf', '#f26b3a', '#2c9c6b', '#1c274c', '#b71c1c', '#fbe5d6', '#ffffff'];

const DesignPanel = ({
  restauranteHeader,
  setRestauranteHeader,
  handleSaveRestauranteHeader,
  savingRestaurante,
  restaurante,
}) => {
  const bgImage = restauranteHeader.cardapio_bg_image_url;
  const bgColor = restauranteHeader.cardapio_bg_color;
  const previewBg = bgImage
    ? `url(${bgImage}) center / cover no-repeat`
    : (bgColor || 'linear-gradient(135deg, #fffaf2 0%, #f6e8d7 100%)');
  const logoSrc = restauranteHeader.cardapio_logo_url || restauranteHeader.foto_url;

  return (
    <Paper
      sx={{
        mb: 3,
        p: { xs: 2, md: 3 },
        border: `1.5px dashed ${menuPalette.border}`,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,250,242,0.9))',
        boxShadow: '0 12px 30px rgba(0,0,0,0.06)',
      }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '0.04em', color: menuPalette.ink }}>
            Design do One Menu
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Defina cor/imagem de fundo, logo e escolha o layout.
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderColor: menuPalette.border, bgcolor: 'rgba(255,255,255,0.9)' }}>
          <Box display="flex" alignItems="center" gap={1} mb={1.25}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: menuPalette.ink }}>
              Pré-visualização
            </Typography>
            <Chip size="small" label="ao vivo" color="success" variant="outlined" sx={{ fontWeight: 700, height: 20 }} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              É assim que seus clientes verão o topo do cardápio
            </Typography>
          </Box>
          <Box
            sx={{
              position: 'relative',
              minHeight: 168,
              borderRadius: 3,
              overflow: 'hidden',
              background: previewBg,
              border: `1px solid ${menuPalette.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2.5,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(2px)',
                borderRadius: 3,
                px: { xs: 2, md: 4 },
                py: 2.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.25,
                boxShadow: '0 12px 30px rgba(28,39,76,0.18)',
                maxWidth: 360,
                width: '100%',
              }}
            >
              {logoSrc ? (
                <Avatar src={logoSrc} sx={{ width: 64, height: 64, border: `3px solid ${menuPalette.border}` }} />
              ) : (
                <Avatar sx={{ width: 64, height: 64, bgcolor: menuPalette.blue }}>
                  <RestaurantMenuOutlined />
                </Avatar>
              )}
              <Typography sx={{ fontWeight: 800, color: menuPalette.ink, textAlign: 'center', lineHeight: 1.2 }}>
                {restauranteHeader.nome.trim() || 'Seu restaurante'}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  border: `1px solid ${menuPalette.border}`,
                  backgroundColor: '#fff',
                  color: 'text.disabled',
                }}
              >
                <Search fontSize="small" sx={{ color: menuPalette.blue }} />
                <Typography variant="caption" sx={{ fontStyle: 'italic' }}>Buscar no cardápio…</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        <TextField
          label="Nome do restaurante"
          value={restauranteHeader.nome}
          onChange={(e) => setRestauranteHeader((s) => ({ ...s, nome: e.target.value }))}
          required
          fullWidth
          inputProps={{ maxLength: 120 }}
          helperText="Este nome aparece no topo do link público do cardápio."
        />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="stretch">
          <Paper variant="outlined" sx={{ p: 2, flex: 1, borderColor: menuPalette.border, bgcolor: 'rgba(255,255,255,0.9)' }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Header</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                label="Cor do header"
                type="color"
                value={restauranteHeader.cardapio_bg_color || '#ffffff'}
                onChange={(e) => setRestauranteHeader((s) => ({ ...s, cardapio_bg_color: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 180 }}
                helperText="Use cor ou uma imagem; limpe para voltar ao gradiente padrão"
              />
              <ImageUpload
                image={restauranteHeader.cardapio_bg_image_url || ''}
                label="Imagem do header"
                recommendedSize="1200 × 500 px"
                outputWidth={1200}
                outputHeight={500}
                previewWidth={240}
                previewHeight={100}
                onImageAdjusted={(adjustedImage) =>
                  setRestauranteHeader((s) => ({ ...s, cardapio_bg_image_url: adjustedImage }))
                }
              />
              <Stack direction={{ xs: 'row', sm: 'column' }} spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => setRestauranteHeader((s) => ({ ...s, cardapio_bg_image_url: '' }))}
                >
                  Remover imagem
                </Button>
                <Button
                  variant="text"
                  onClick={() => setRestauranteHeader((s) => ({ ...s, cardapio_bg_color: '' }))}
                >
                  Limpar cor
                </Button>
              </Stack>
            </Stack>
            <Box mt={2}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.75 }}>
                Cores rápidas
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {HEADER_COLOR_PRESETS.map((preset) => {
                  const selected = (restauranteHeader.cardapio_bg_color || '').toLowerCase() === preset.toLowerCase();
                  return (
                    <Tooltip key={preset} title={preset}>
                      <Box
                        role="button"
                        aria-label={`Usar cor ${preset}`}
                        onClick={() => setRestauranteHeader((s) => ({ ...s, cardapio_bg_color: preset }))}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          backgroundColor: preset,
                          cursor: 'pointer',
                          border: selected ? `2px solid ${menuPalette.blue}` : '1px solid rgba(28,39,76,0.2)',
                          boxShadow: selected ? '0 0 0 3px rgba(31,79,191,0.2)' : '0 2px 6px rgba(28,39,76,0.15)',
                          transition: 'transform .15s ease, box-shadow .15s ease',
                          '&:hover': { transform: 'scale(1.12)' },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, width: { xs: '100%', md: 320 }, borderColor: menuPalette.border, bgcolor: 'rgba(255,255,255,0.9)' }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Logo e layout</Typography>
            <Stack spacing={1} alignItems="flex-start">
              <ImageUpload
                image={restauranteHeader.cardapio_logo_url || restauranteHeader.foto_url}
                label="Logo"
                recommendedSize="512 × 512 px (formato quadrado)"
                outputWidth={512}
                outputHeight={512}
                outputMimeType="image/png"
                previewWidth={140}
                previewHeight={140}
                onImageAdjusted={(adjustedImage) =>
                  setRestauranteHeader((s) => ({ ...s, cardapio_logo_url: adjustedImage }))
                }
              />
              <Stack spacing={1}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setRestauranteHeader((s) => ({ ...s, cardapio_logo_url: '' }))}
                >
                  Remover logo
                </Button>
              </Stack>
            </Stack>
            <Stack spacing={1} mt={2}>
              <Typography variant="subtitle1" fontWeight={700}>Layout do cardápio</Typography>
              <TextField
                select
                label="Layout"
                value={restauranteHeader.cardapio_layout || 'modern'}
                onChange={(e) => setRestauranteHeader((s) => ({ ...s, cardapio_layout: e.target.value }))}
                fullWidth
                helperText="Estilo visual da página pública do cardápio"
              >
                <MenuItem value="modern">Moderno</MenuItem>
                <MenuItem value="retro">Retrô</MenuItem>
              </TextField>
            </Stack>
          </Paper>
        </Stack>

        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleSaveRestauranteHeader}
            disabled={savingRestaurante || !restaurante?.id}
            sx={{ px: 3, fontWeight: 800 }}
          >
            {savingRestaurante ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default DesignPanel;
